/**
 * 美編生圖 Dialog(原 REQ-20260429001 prompt 產生器 → 直接生圖,2026-05-14)。
 *
 * 流程:
 *   1) 選方向 + 補充說明 → 按「產生圖」
 *   2) client 端用 templates.ts 組 prompt → POST /design-image/generate
 *      → 後端 fetch 來源附件 bytes,呼叫 OpenAI /v1/images/edits → 回 b64 PNG
 *   3) 顯示 preview;按「儲存到訪談」走既有 uploadAttachment 3-step 流程
 *   4) 或按「重新產生」回 step 1
 *
 * 沿用原有的設計方向模板(templates.ts);需要 OpenAI 生圖設定已啟用,
 * 否則後端 503,前端顯示提示。
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/Modal';
import { fetchDownloadUrl, uploadAttachment } from '../attachments/api';
import { b64ToPngFile, useGenerateDesignImage } from './api';
import {
  DESIGN_TEMPLATES,
  groupLabels,
  type DesignTemplateGroup,
} from './templates';

interface Props {
  open: boolean;
  onClose: () => void;
  attachment: { uuid: string; originalName: string };
  /** 生圖後儲存的目標訪談;同時用於 invalidate ['interviews', customerId]。 */
  interviewId: number;
  customerId: number;
}

const GROUP_ORDER: DesignTemplateGroup[] = ['ad', 'social', 'customer'];

/**
 * 取來源檔名(去副檔名)當生成檔名前綴,加時間戳 + .png。
 * 例:`0225912345莊頭北_單張型錄_A.png` → `0225912345莊頭北_單張型錄_A-design-20260514-153000.png`
 */
function buildGeneratedName(sourceName: string): string {
  const base = sourceName.replace(/\.[^.]+$/, '');
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  return `${base}-design-${stamp}.png`;
}

export function DesignPromptDialog({
  open,
  onClose,
  attachment,
  interviewId,
  customerId,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const generateMutation = useGenerateDesignImage();

  const [templateId, setTemplateId] = useState<string>(DESIGN_TEMPLATES[0]?.id ?? '');
  const [note, setNote] = useState('');
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);
  /** 已生成的 b64 PNG;null = 還在 idle 或剛重置。 */
  const [generatedB64, setGeneratedB64] = useState<string | null>(null);
  const [generatedMeta, setGeneratedMeta] = useState<{ model: string; ms: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // 開啟時抓縮圖;關閉時 reset
  useEffect(() => {
    if (!open) {
      setTemplateId(DESIGN_TEMPLATES[0]?.id ?? '');
      setNote('');
      setThumbUrl(null);
      setThumbError(false);
      setGeneratedB64(null);
      setGeneratedMeta(null);
      setSaving(false);
      setSaveError(null);
      setSavedFlash(null);
      generateMutation.reset();
      return;
    }
    let cancelled = false;
    setThumbError(false);
    fetchDownloadUrl(attachment.uuid)
      .then((url) => {
        if (!cancelled) setThumbUrl(url);
      })
      .catch(() => {
        if (!cancelled) setThumbError(true);
      });
    return () => {
      cancelled = true;
    };
    // 刻意省略部分 deps(只在指定值變動時重跑)。
    // 註:專案未安裝 react-hooks plugin,原本這裡的 disable 指令引用了不存在的
    // 規則,反而被 ESLint 判為錯誤,故移除。導入該 plugin 時要記得補回來。
  }, [open, attachment.uuid]);

  const groupedTemplates = useMemo(() => {
    return GROUP_ORDER.map((g) => ({
      group: g,
      label: groupLabels[g],
      items: DESIGN_TEMPLATES.filter((t) => t.group === g),
    }));
  }, []);

  const generating = generateMutation.isPending;
  const generateError = generateMutation.error;

  // 已等秒數:給 loading 動畫 + 進度條用,讓使用者一眼看出系統在跑
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!generating) {
      setElapsedSec(0);
      return undefined;
    }
    const start = Date.now();
    setElapsedSec(0);
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [generating]);

  const handleGenerate = (): void => {
    const tpl = DESIGN_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    const prompt = tpl.build(note);
    setSaveError(null);
    setSavedFlash(null);
    generateMutation.mutate(
      { sourceAttachmentUuid: attachment.uuid, prompt },
      {
        onSuccess: (data) => {
          setGeneratedB64(data.b64Image);
          setGeneratedMeta({ model: data.model, ms: data.latencyMs });
        },
      },
    );
  };

  const handleRegenerate = (): void => {
    setGeneratedB64(null);
    setGeneratedMeta(null);
    generateMutation.reset();
  };

  const handleSave = async (): Promise<void> => {
    if (!generatedB64) return;
    setSaving(true);
    setSaveError(null);
    try {
      const file = b64ToPngFile(generatedB64, buildGeneratedName(attachment.originalName));
      await uploadAttachment(interviewId, file);
      void qc.invalidateQueries({ queryKey: ['interviews', customerId] });
      setSavedFlash('✓ 已儲存到訪談附件');
      // 給使用者看到 flash 再自動關
      window.setTimeout(() => onClose(), 800);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const busy = generating || saving;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="✨ 美編生圖"
      widthClass="max-w-3xl"
      dismissOnBackdrop={!busy && !generatedB64 /* 已有結果時不要誤關 */}
    >
      {/* 原稿縮圖 */}
      <div className="flex items-center gap-3 mb-4 p-2 bg-slate-50 rounded border border-slate-200">
        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center overflow-hidden shrink-0">
          {thumbError ? (
            <span className="text-xs text-slate-400">📷</span>
          ) : thumbUrl ? (
            <img
              src={thumbUrl}
              alt={attachment.originalName}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xs text-slate-400">…</span>
          )}
        </div>
        <div className="text-sm text-slate-700 truncate">
          <div className="text-xs text-slate-500">原稿</div>
          <div className="font-medium truncate">{attachment.originalName}</div>
        </div>
      </div>

      {/* 結果區(產生後出現) */}
      {generatedB64 ? (
        <section
          className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded grid gap-3"
          data-testid="design-image-result"
        >
          <img
            src={`data:image/png;base64,${generatedB64}`}
            alt="generated"
            className="max-w-full max-h-[60vh] mx-auto rounded border border-slate-300 bg-white"
            data-testid="design-image-preview"
          />
          <div className="text-xs text-slate-600 text-center">
            {generatedMeta
              ? `${generatedMeta.model} · ${(generatedMeta.ms / 1000).toFixed(1)}s`
              : ''}
          </div>

          {saveError ? (
            <div role="alert" className="text-red-600 text-sm text-center">
              儲存失敗:{saveError}
            </div>
          ) : null}
          {savedFlash ? (
            <div role="status" className="text-green-700 text-sm text-center">
              {savedFlash}
            </div>
          ) : null}

          <div className="flex gap-2 justify-center flex-wrap">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || Boolean(savedFlash)}
              className="bg-violet-600 text-white rounded px-4 py-1.5 text-sm hover:bg-violet-700 disabled:opacity-50"
              data-testid="design-image-save"
            >
              {saving ? '儲存中…' : '💾 儲存到訪談'}
            </button>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={saving || generating}
              className="border border-violet-300 bg-white text-violet-700 rounded px-4 py-1.5 text-sm hover:bg-violet-50 disabled:opacity-50"
              data-testid="design-image-regenerate"
            >
              🔄 重新產生
            </button>
          </div>
        </section>
      ) : null}

      {/* 表單區(已有結果時隱藏,要重新產生才會回來) */}
      {!generatedB64 ? (
        <>
          <fieldset className="mb-4" disabled={generating}>
            <legend className="text-sm font-semibold mb-2">處理方向</legend>
            <div className="space-y-3">
              {groupedTemplates.map(({ group, label, items }) => (
                <div key={group}>
                  <div className="text-xs text-slate-500 mb-1">{label}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {items.map((t) => (
                      <label
                        key={t.id}
                        className={`flex items-center gap-2 text-sm border rounded px-2 py-1.5 cursor-pointer ${
                          templateId === t.id
                            ? 'bg-violet-50 border-violet-400'
                            : 'border-slate-200 hover:bg-slate-50'
                        } ${generating ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="design-template"
                          value={t.id}
                          checked={templateId === t.id}
                          onChange={() => setTemplateId(t.id)}
                          disabled={generating}
                          data-testid={`design-template-${t.id}`}
                        />
                        <span>{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-1" htmlFor="design-note">
              補充說明<span className="text-slate-400 font-normal">(選填)</span>
            </label>
            <textarea
              id="design-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              disabled={generating}
              placeholder="例如:主色調暖色、加入「春季新品」字樣、商品要居左"
              className="w-full border border-slate-300 rounded p-2 text-sm disabled:bg-slate-50"
              data-testid="design-prompt-note"
            />
          </div>

          {generateError ? (
            <div role="alert" className="mb-3 text-red-600 text-sm">
              生圖失敗:
              {generateError instanceof Error ? generateError.message : '未知錯誤'}
            </div>
          ) : null}
          {generating ? (
            <GeneratingIndicator elapsedSec={elapsedSec} />
          ) : null}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={generating}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-50"
            >
              關閉
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="bg-violet-600 text-white rounded px-3 py-1.5 text-sm hover:bg-violet-700 disabled:opacity-50"
              data-testid="design-image-generate"
            >
              {generating ? '生成中…' : '✨ 產生圖'}
            </button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

/**
 * 生成中視覺指示:轉圈 spinner + 已等秒數 + 漸進式進度條。
 * 進度條以 30s 為基準預估,封頂 95%(避免假裝完成),搭配 elapsedSec 給使用者
 * 「系統在跑」的明確訊號。
 */
function GeneratingIndicator({ elapsedSec }: { elapsedSec: number }): JSX.Element {
  const ESTIMATED_SEC = 30;
  const percent = Math.min(95, Math.round((elapsedSec / ESTIMATED_SEC) * 100));
  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-4 flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-md"
      data-testid="design-image-generating"
    >
      {/* 旋轉 spinner:雙環,外環淡色背景,內環 violet 旋轉 */}
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-4 border-violet-200" />
        <div className="absolute inset-0 rounded-full border-4 border-violet-600 border-t-transparent animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center text-base">
          ✨
        </div>
      </div>

      <div className="text-center">
        <div className="text-violet-900 font-medium text-sm">生成中…</div>
        <div className="text-xs text-violet-700 mt-0.5">
          已等 <span className="font-mono tabular-nums">{elapsedSec}s</span>
          {' · '}OpenAI 通常 10–30 秒,複雜 prompt 可能到 90 秒
        </div>
      </div>

      {/* 進度條(模擬,非 server 推送) */}
      <div className="w-full max-w-sm h-1.5 bg-violet-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
